import { useEffect, useState } from "react";
import { Loader2, Save, Sparkles, ImagePlus, Leaf } from "lucide-react";
import {
  useAppSettings,
  useUpdateAppSettings,
  uploadAppLogo,
  removeAppLogo,
} from "../api";
import { useAuthStore } from "@shared/store/auth";

export function MarcaPage() {
  const { data, isLoading } = useAppSettings();
  const updateMut = useUpdateAppSettings();
  const refreshMe = useAuthStore((s) => s.refresh);

  const [nombre, setNombre] = useState("");
  const [eslogan, setEslogan] = useState("");
  const [moneda, setMoneda] = useState("EUR");
  const [formatoFecha, setFormatoFecha] = useState("d/m/Y");
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [ok, setOk] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!data) return;
    setNombre(data.plataforma_nombre ?? "");
    setEslogan(data.plataforma_eslogan ?? "");
    setMoneda(data.moneda ?? "EUR");
    setFormatoFecha(data.formato_fecha ?? "d/m/Y");
    setLogoUrl(data.plataforma_logo_url ?? null);
  }, [data]);

  async function guardar(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setOk(false);
    try {
      await updateMut.mutateAsync({
        plataforma_nombre: nombre,
        plataforma_eslogan: eslogan,
        moneda,
        formato_fecha: formatoFecha,
      });
      await refreshMe();
      setOk(true);
      setTimeout(() => setOk(false), 2500);
    } catch {
      setError("No se pudieron guardar los cambios.");
    }
  }

  async function onLogo(file: File | null) {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const { url } = await uploadAppLogo(file);
      setLogoUrl(url);
      await refreshMe();
    } catch {
      setError("No se pudo subir el logo (máx. 4 MB).");
    } finally {
      setUploading(false);
    }
  }

  async function quitarLogo() {
    setUploading(true);
    try {
      await removeAppLogo();
      setLogoUrl(null);
      await refreshMe();
    } finally {
      setUploading(false);
    }
  }

  if (isLoading) {
    return (
      <div className="p-8 flex items-center gap-2 text-slate-500">
        <Loader2 className="animate-spin" size={18} /> Cargando…
      </div>
    );
  }

  return (
    <div className="p-8 h-full flex flex-col gap-6 min-h-0 overflow-auto">
      <header>
        <h1 className="text-3xl font-black flex items-center gap-3 text-slate-900">
          <Sparkles size={28} className="text-brand-700" /> Marca de la plataforma
        </h1>
        <p className="text-slate-500 mt-1">
          Personaliza el nombre, eslogan y logo que ve tu equipo en toda la aplicación.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Formulario */}
        <form onSubmit={guardar} className="lg:col-span-2 bg-white rounded-2xl shadow-soft border border-slate-200/70 p-6 flex flex-col gap-5">
          {error && (
            <div className="rounded-lg bg-rose-50 border border-rose-200 text-rose-700 px-3 py-2 text-sm">
              {error}
            </div>
          )}
          {ok && (
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 px-3 py-2 text-sm">
              Cambios guardados ✓
            </div>
          )}

          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
              Nombre de la plataforma
            </label>
            <input
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              maxLength={60}
              placeholder="INPRONEXUS"
              className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
              Eslogan
            </label>
            <input
              value={eslogan}
              onChange={(e) => setEslogan(e.target.value)}
              maxLength={80}
              placeholder="People CRM Suite"
              className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
              Logo
            </label>
            <div className="flex items-center gap-4 mt-1">
              <div className="h-16 w-16 shrink-0 rounded-2xl bg-[linear-gradient(135deg,#00d09c,#00c3af)] grid place-items-center overflow-hidden text-white">
                {logoUrl ? (
                  <img src={logoUrl} alt="Logo" className="max-h-full max-w-full object-contain" />
                ) : (
                  <Leaf size={26} />
                )}
              </div>
              <div className="flex flex-col gap-2">
                <label className="inline-flex items-center gap-2 cursor-pointer rounded-lg border border-slate-300 hover:bg-slate-50 px-3 py-2 text-sm font-bold text-slate-700 w-fit">
                  {uploading ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />}
                  {uploading ? "Subiendo…" : "Subir logo"}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
                    className="hidden"
                    disabled={uploading}
                    onChange={(e) => onLogo(e.target.files?.[0] ?? null)}
                  />
                </label>
                {logoUrl && (
                  <button type="button" onClick={quitarLogo} className="text-xs font-bold text-rose-600 hover:text-rose-800 w-fit">
                    Quitar logo
                  </button>
                )}
                <span className="text-xs text-slate-400">PNG, SVG o JPG · máx. 4 MB</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Moneda</label>
              <select
                value={moneda}
                onChange={(e) => setMoneda(e.target.value)}
                className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
              >
                <option value="EUR">Euro (€)</option>
                <option value="USD">Dólar ($)</option>
                <option value="GBP">Libra (£)</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Formato de fecha</label>
              <select
                value={formatoFecha}
                onChange={(e) => setFormatoFecha(e.target.value)}
                className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
              >
                <option value="d/m/Y">31/12/2026</option>
                <option value="Y-m-d">2026-12-31</option>
                <option value="m/d/Y">12/31/2026</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={updateMut.isPending}
              className="inline-flex items-center gap-2 rounded-lg bg-brand-700 hover:bg-brand-800 text-white px-4 py-2 text-sm font-bold disabled:opacity-50"
            >
              <Save size={14} /> Guardar cambios
            </button>
          </div>
        </form>

        {/* Vista previa */}
        <div className="bg-white rounded-2xl shadow-soft border border-slate-200/70 p-6">
          <div className="text-xs font-black uppercase tracking-wider text-slate-400 mb-3">Vista previa</div>
          <div className="rounded-2xl bg-[linear-gradient(180deg,#0b4a2f,#0a3f28)] p-4">
            <div className="flex items-center gap-3">
              <div className="grid h-12 w-12 shrink-0 place-items-center rounded-[18px] bg-[linear-gradient(135deg,#00d09c,#00c3af)] text-white overflow-hidden">
                {logoUrl ? (
                  <img src={logoUrl} alt="Logo" className="max-h-full max-w-full object-contain" />
                ) : (
                  <Leaf size={22} />
                )}
              </div>
              <div className="min-w-0">
                <div className="truncate text-[20px] font-black tracking-[-0.05em] text-white">
                  {nombre || "INPRONEXUS"}
                </div>
                <div className="mt-0.5 text-[10px] font-black uppercase tracking-[0.22em] text-white/60 truncate">
                  {eslogan || "People CRM Suite"}
                </div>
              </div>
            </div>
          </div>
          <p className="mt-3 text-xs text-slate-500">
            Así se verá en el menú lateral para todo tu equipo.
          </p>
        </div>
      </div>
    </div>
  );
}
